/* eslint-disable unicorn/filename-case -- TODO rename later */
import {
  AppIntegration,
  AuthIntegration,
  AuthIntegrationType,
  FunctionAuthType,
  IntegrationType,
  NewAppIntegrationFromJSON,
  PageAuthIntegration,
} from "@istari/istari-client";
import { useMutation, useQuery, UseQueryResult } from "@tanstack/react-query";

import { ListOptions } from "~/api/model/modelQueryKeys";
import { lookupOrFetchUser } from "~/api/useModels";
import { getOrginizations } from "~/external-api/admin";
import { underscoreToSpaced } from "~/lib/utils";

import showToast from "../components/__deprecated/Toast";
import clientSDK from "./clientSDK";

export type AuthInfoType = { authIntegrationType: AuthIntegrationType; authType: FunctionAuthType; key: string };

export interface AuthIntData {
  id: string;
  authIntegrationType: AuthIntegrationType;
  authType: FunctionAuthType;
  createdAt: Date;
  createdBy: string;
  appId?: string | null;
  registration: Record<string, string>;
  name: string;
}

export interface AppIntData {
  id: string;
  integrationType: IntegrationType;
  integrationData: Record<string, string> | null;
  createdAt: Date;
  creatorName?: string;
  authIntegrations: AuthIntData[];
  description?: string | null;
  name: string;
}

export interface AppIntPageData {
  items: AppIntData[];
  total: number;
}

export interface NewAppIntegrationData {
  appData: {
    description: string;
    integrationType: IntegrationType;
    integrationData: object | undefined;
  };

  authDataList: {
    name: string;
    authIntegrationType: AuthIntegrationType;
    file: File;
    functionAuthType: FunctionAuthType;
  }[];
}

export const functionAuthQueryKeys = {
  getTenantPublicKey: () => ["getTenantPublicKey"] as const,
  listAppIntegrations: (filter: ListOptions) => ["listAppIntegrations", filter],
};

async function getRegistrationContent(authInt: AuthIntegration): Promise<Record<string, string>> {
  const content = await authInt.readCurrentContent();
  const blob = new Blob([new Uint8Array(content)]);
  const text = await blob?.text();
  return JSON.parse(text);
}

async function authApiToTableData(authInt: AuthIntegration): Promise<AuthIntData> {
  return {
    id: authInt.id,
    authIntegrationType: authInt.authIntegrationType,
    authType: authInt.authType,
    createdAt: authInt.created,
    createdBy: authInt.createdById,
    appId: authInt.appIntegrationId,
    registration: await getRegistrationContent(authInt),
    name: underscoreToSpaced(authInt.authIntegrationType),
  };
}

export const getAuthIntegration = (authInfo: AuthInfoType) =>
  clientSDK
    .listAuthIntegrations(authInfo.authIntegrationType, authInfo.authType)
    .then((page: PageAuthIntegration) => page.items.filter((auth) => auth.appIntegrationId)[0])
    .then(async (authInt) => await authApiToTableData(authInt))
    .catch((err: Error) => {
      showToast("Could not get function auth provider.", "error");
      throw err;
    });

export const addFunctionAuthSecret = async (
  file: File,
  authIntegrationId: string,
  authType: FunctionAuthType,
  secretExp: Date,
) => clientSDK.addFunctionAuthSecretLazy(file, authType, authIntegrationId, secretExp);

async function appToTableData(app: AppIntegration): Promise<AppIntData> {
  const authIntegrations = app.authIntegrations ? app.authIntegrations : [];
  return {
    id: app.id,
    integrationType: app.integrationType,
    integrationData: app.integrationData,
    createdAt: app.created,
    creatorName: await lookupOrFetchUser(app.createdById),
    authIntegrations: await Promise.all(authIntegrations.map((auth) => authApiToTableData(auth))),
    description: app.description,
    name: underscoreToSpaced(app.integrationType),
  };
}

export const useListAppIntegrations = ({ page, size, sort }: ListOptions): UseQueryResult<AppIntPageData> =>
  useQuery({
    queryKey: functionAuthQueryKeys.listAppIntegrations({ page, size, sort }),
    queryFn: () =>
      clientSDK.listAppIntegrations(page, size, sort).then(async (appInt) => {
        return {
          items: await Promise.all(appInt.items.map((item) => appToTableData(item))),
          total: appInt.total,
        };
      }),
  });

export const useDeleteAppIntegration = (options?: {
  onSuccess?: (id: string) => void;
  onError?: (error: unknown) => void;
}) =>
  useMutation({
    mutationFn: async (appId: string) => {
      const deleted = await clientSDK.deleteAppIntegration(appId);
      return deleted.id;
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });

export const useAddAppIntegration = (options?: { onSuccess?: () => void; onError?: (error: unknown) => void }) =>
  useMutation({
    mutationFn: async (data: NewAppIntegrationData) => {
      const { appData, authDataList } = data;
      const appInt = await clientSDK.createAppIntegration(
        NewAppIntegrationFromJSON({
          description: appData.description,
          integration_type: appData.integrationType,
          integration_data: appData.integrationData,
        }),
      );
      for (const authData of authDataList) {
        await clientSDK.createAuthIntegrationLazy(
          authData.authIntegrationType,
          authData.functionAuthType,
          appInt.id,
          authData.file,
        );
      }
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });

export const useGetPublicKey = () =>
  useQuery({
    queryKey: functionAuthQueryKeys.getTenantPublicKey(),
    queryFn: () =>
      clientSDK
        .getTenantPublicKey()
        .then(async (publicKey) => ({
          createdBy: await lookupOrFetchUser(publicKey.createdById),
          createdAt: publicKey.created,
          orgs: await getOrginizations(),
          key: publicKey.publicKey,
        }))
        .catch((err) => {
          if (err.response?.status === 404) return null;
          throw err;
        }),
  });

export const useAddPublicKey = (options?: { onSuccess?: () => void; onError?: (error: unknown) => void }) =>
  useMutation({
    mutationFn: (publicKey: File) => clientSDK.createTenantPublicKey(publicKey),
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });

export const useDeletePublicKey = (options?: { onSuccess?: () => void; onError?: (error: unknown) => void }) =>
  useMutation({
    mutationFn: () => clientSDK.deleteTenantPublicKey(),
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
